import React, { useState } from 'react'
import { Card, Icon, ScheduleCard, Spinner, Typography } from '@telavita-core/react-design-kit'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'

import { DASHBOARD_APPOINTMENTS } from 'settings/_routesSettings'

import { modalTypes } from 'containers/ModalManager/ModalManager'
import ToggleFloatButton from 'components/ToggleFloatButton'

import { hideModal, showModal } from 'redux/actions'

import { useScheduleToken } from 'hooks/useScheduleToken'

import { DATE_DISPLAYER_STATUS } from 'settings/_dateDisplayerStatusSettings'
import { TVTVPACI, TVTVPSIC, TVTVPSIQ } from 'settings/_profileSettings'

import { RequestTokenModal } from 'components'
import { INextSchedule } from 'services/me/nextSchedules/types'

import { ReactComponent as ExclamationIcon } from 'static/svg/exclamation-icon.svg'
import { useEligibleToSchedule } from './useEligibleToSchedule'

interface AuthState {
  auth: {
    userData: {
      username: string
      person_general: {
        full_name: string
      }
    }
  }
}

interface NextScheduleCardProps {
  schedule: INextSchedule
  loading: boolean
  getNextSchedule: () => void
}

const EligibleToScheduleWarning = () => (
  <div style={{ width: '100%' }} className='ConsultationCard__FeedbackWarning'>
    <div>
      <ExclamationIcon />
    </div>
    <Typography variant='content3' color='danger'>
      Se até a data da próxima consulta seu nome ainda não constar na lista de beneficiários do convênio, 
      todos os próximos agendamentos serão cancelados.
    </Typography>
  </div>
)

const NextScheduleCard = ({
  schedule,
  loading,
  getNextSchedule
}: NextScheduleCardProps): JSX.Element => {
  const dispatch = useDispatch()

  const { eligibleToSchedule } = useEligibleToSchedule(schedule)

  const {
    username,
    person_general: {
      full_name
    }
  } = useSelector((state: AuthState) => state?.auth?.userData)

  const { openTokenModal, onCloseModal, onClickJoinVirtualRoom } = useScheduleToken({
    schedule: {
      endDate: schedule.endDate,
      startDate: schedule.startDate,
      tokenHasBeenFilled: schedule.tokenHasBeenFilled,
      virtualRoomHash: schedule.virtualRoomHash,
      planGroupCode: schedule.planGroupCode,
    }
  })

  const [scheduleStarted, setScheduleStarted] = useState(false)

  const hasSchedule = Object.keys(schedule).length > 0

  const buildScheduleMenuOptions = () => {

    const isPsychiatrist = schedule.professionalProfileCode === TVTVPSIQ
    const timeText = isPsychiatrist ? '1 hora' : '12 horas' 
    const blockedModalTitle = `Desculpe, não é possível\n reagendar ou cancelar com\n menos de ${timeText} do\n horário da consulta`

    const blockedModalData = {
      modalTitle: blockedModalTitle,
      titleColor: 'false',
      btnText: 'ENTENDI',
      handleClick: () => dispatch(hideModal()),
      modalChildren: (
        <div>
          <br /><Typography>Esta regra existe para garantir o compromisso entre paciente e profissional quanto a programação de agendas.</Typography>
          <br /><Typography>Procure reagendar / cancelar com antecedência.</Typography>
        </div>
      ),
    }

    const rescheduleData = {
      dashboard: 'PACI',
      client: {
        username: username,
        profileCode: TVTVPACI,
        name: full_name
      },
      professional: {
        document: schedule.professionalDocumentValue,
        name: schedule.professionalFullName,
        profileCode: schedule.professionalProfileCode,
        username: schedule.professionalUsername
      },
      schedule: {
        ...schedule,
        plan_code: schedule.planCode,
        date: format(new Date(schedule.startDate), 'dd/MM/yyyy'),
        time: format(new Date(schedule.startDate), 'HH\'h\'mm'),
        isoDate: schedule.startDate
      },
      product: {
        id: 1,
        code: schedule.productCode
      },
    }

    const cancelModalData = {
      scheduleId: schedule?.id,
      requesterUsername: username,
      onSuccessClose: () => {
        getNextSchedule()
      }
    }
    return [
      {
        code: 'reschedule-appointment',
        name: 'Reagendar',
        func: () => schedule.rescheduleEnabled ? dispatch(showModal(modalTypes.RESCHEDULE, rescheduleData)) : dispatch(showModal(modalTypes.GENERIC, blockedModalData))
      },
      {
        code: 'cancel-appointment',
        name: 'Cancelar',
        func: () => schedule.cancellationEnabled ? dispatch(showModal(modalTypes.USER_CANCEL, cancelModalData)) : dispatch(showModal(modalTypes.GENERIC, blockedModalData))
      }
    ]
  }

  function getProfessionalProfileName(value: string) {
    return value === TVTVPSIC ? 'Psicólogo(a)' : 'Médico(a)'
  }

  return (
    <Card
      customClassName='PatientHome__Card'
      variant={scheduleStarted ? 'warning' : 'regular'}
    >
      <Typography variant='content1' inlineStyles={{ fontWeight: 'bold' }}>
        Próxima consulta
      </Typography>

      {loading &&
        <div className='PatientHome__NextScheduleEmpty'>
          <Spinner />
        </div>
      }

      {!hasSchedule && !loading && (
        <div className='PatientHome__NextScheduleEmpty'>
          <Icon name='Calendar' />
          <Typography variant='content3' inheritColor>
            Nenhuma consulta marcada
          </Typography>
        </div>
      )}

      {hasSchedule && !loading
        ? (
          <div className='PatientHome__NextScheduleCard'>
            <ScheduleCard
              id={String(schedule.id)}
              plan={schedule.planName}
              start_date={schedule.startDate}
              status_name={DATE_DISPLAYER_STATUS[schedule.statusCode].label}
              session_number={schedule.session ? String(schedule.session) : ''} 
              professional_name={schedule.professionalFullName}
              professional_picture_url={schedule.professionalPictureUrl}
              professional_specialty={getProfessionalProfileName(schedule.professionalProfileCode)}
              professional_document={schedule.professionalDocumentValue}
              onScheduleStarted={() => setScheduleStarted(true)}
              onClickJoin={onClickJoinVirtualRoom}
              showMedicalReturnBadge={schedule.medicalReturn}
              showActions={schedule.cancellationEnabled || schedule.rescheduleEnabled}
              actionsComponent={
                <ToggleFloatButton
                  menuList={buildScheduleMenuOptions()}
                />
              }
            />
          </div>
        )
        : null
      }

      {(hasSchedule && !eligibleToSchedule) && <EligibleToScheduleWarning />}

      <Link to={DASHBOARD_APPOINTMENTS}>
        <Typography variant='content2' link inlineStyles={{ fontWeight: 'bold' }}>
          Ver todas as consultas
        </Typography>
      </Link>

      {openTokenModal && (
        <RequestTokenModal
          planGroupName={schedule.planGroupName}
          planGroupCode={schedule.planGroupCode}
          onClose={onCloseModal}
          scheduleId={schedule.id}
          onClickJoinVirtualRoom={onClickJoinVirtualRoom}
        />
      )}

    </Card>
  )
}

export { NextScheduleCard }
